///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Translucent walls and floor of the storm column box above the sea
/// surface. The two far walls draw (chosen per frame by the parent from
/// the camera position); the shader rules them in kilometers and pressure
/// levels and frames them.
///

import { type FarWalls } from "@/earthbrowser/dataset-distribution/earthquakes/cutout/CutoutWallsNode"
import { niceStep } from "@/earthbrowser/dataset-distribution/earthquakes/cutout/cutoutLayout"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { vec4 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { boxHeightKm, cPressureLevels, type StormColumnLayout } from "./stormColumnLayout"

export class StormColumnWallsNode extends NodeBase {
	private readonly wallsRenderer: StormColumnWallsRenderer
	/** Horizontal grid spacing chosen for the footprint, km */
	public readonly gridKm: number

	constructor(context: StovokorContext, layout: StormColumnLayout) {
		super()
		const geometry = new StormColumnWallsGeometry(context, layout)
		this.wallsRenderer = new StormColumnWallsRenderer(context, geometry)
		this.wallsRenderer.halfX.value = layout.lengthKm / 2
		this.wallsRenderer.halfZ.value = layout.widthKm / 2
		this.wallsRenderer.boxHeight.value = boxHeightKm(layout)
		this.gridKm = niceStep(layout.lengthKm / 12)
		this.wallsRenderer.tick.value = this.gridKm
		this.wallsRenderer.heightTick.value = layout.tickKm * layout.heightScale
		const p = cPressureLevels.map(level => level.km * layout.heightScale)
		this.wallsRenderer.pressureLevels.value = new vec4(p[0], p[1], p[2], p[3])
		this.renderer = this.wallsRenderer
	}

	public set opacity(value: number) {
		this.wallsRenderer.opacity.value = value
	}

	public setFarWalls(far: FarWalls): void {
		this.wallsRenderer.visibleWalls.value = new vec4(
			far.signX > 0 ? 1 : 0,
			far.signX > 0 ? 0 : 1,
			far.signZ > 0 ? 1 : 0,
			far.signZ > 0 ? 0 : 1
		)
	}
}

class StormColumnWallsRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public visibleWalls = new UniformDynamic<vec4>(new vec4(1, 0, 1, 0))
	public halfX = new UniformDynamic<number>(1)
	public halfZ = new UniformDynamic<number>(1)
	public boxHeight = new UniformDynamic<number>(1)
	public tick = new UniformDynamic<number>(1)
	public heightTick = new UniformDynamic<number>(1)
	public pressureLevels = new UniformDynamic<vec4>(new vec4(0, 0, 0, 0))
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("storm_column_wall"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class StormColumnWallsGeometry implements Geometry {
	public position: VertexAttribute
	public wallId: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true

	constructor(context: StovokorContext, layout: StormColumnLayout) {
		this.position = new VertexAttribute(context, 3)
		this.wallId = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.wallId])

		const hx = layout.lengthKm / 2
		const hz = layout.widthKm / 2
		const top = boxHeightKm(layout)

		const quad = (id: number, a: number[], b: number[], c: number[], d: number[]): void => {
			const base = this.position.count
			for (const p of [a, b, c, d]) {
				this.position.addPos(p[0], p[1], p[2])
				this.wallId.add(id)
			}
			this.indexBuffer.quad(base, base + 1, base + 2, base + 3)
		}
		quad(0, [-hx, top, -hz], [-hx, top, hz], [-hx, 0, hz], [-hx, 0, -hz])
		quad(1, [hx, top, -hz], [hx, top, hz], [hx, 0, hz], [hx, 0, -hz])
		quad(2, [-hx, top, -hz], [hx, top, -hz], [hx, 0, -hz], [-hx, 0, -hz])
		quad(3, [-hx, top, hz], [hx, top, hz], [hx, 0, hz], [-hx, 0, hz])
		quad(4, [-hx, 0, -hz], [hx, 0, -hz], [hx, 0, hz], [-hx, 0, hz])

		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
